import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../../../../components/TopNavbar';
import SideNavbar from '../../../../components/SideNavbar';
import { useAppSelector } from '../../../../redux/store';

// Firebase
import { functions } from '../../../../firebaseConfig'; // Adjust the path based on your project structure
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db } from '../../../../firebaseConfig';
import { fetchInvoices, updateInvoice } from '../Function/AccountFunctions';

interface Invoice {
    id: string;
    data: {
        customer: {
            firstName: string,
            lastName: string
        }, // Replace with an actual UID for testing
        invoiceNumber: string,
        orderNumber: string,
        invoiceDate: string,
        terms: string,
        dueDate: string,
        status?: string,
        receivedAmount?: number;
        subject: string,
        table: [
            {
                itemDetails: string,
                quantity: number,
                rate: number,
                tax: string,
                amount: number, // Includes tax
            },
        ],
        customerNotes: string,
    };
}

interface FetchInvoicesResponse {
    success: boolean;
    message: string;
    invoices: Invoice[];
    hasMore: boolean;
    lastDocument: {
        id: string;
        invoiceDate: string;
    } | null;
}

const InvoiceListAccount: React.FC = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState("Invoice List");
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [invoiceStatuses, setInvoiceStatuses] = useState<{ [key: string]: string }>({});
    const [receivedAmounts, setReceivedAmounts] = useState<{ [key: string]: string }>({});
    const statusOptions = ["Received", "Pending", "Overdue"];
    const sections = ["Invoices", "Payouts", "Invoice List"];
    const invoiceData = [
        {
            date: "Dec 9, 2024",
            invoiceNumber: "INV-001",
            orderNumber: "ORD-001",
            customerName: "John Doe",
            status: "Paid",
            dueDate: "Dec 16, 2024",
            amount: "$1,000",
            balance: "$0",
        },
        {
            date: "Dec 10, 2024",
            invoiceNumber: "INV-002",
            orderNumber: "ORD-002",
            customerName: "Jane Smith",
            status: "Pending",
            dueDate: "Dec 17, 2024",
            amount: "$750",
            balance: "$750",
        }
    ];

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [lastDocument, setLastDocument] = useState<{
        id: string;
        invoiceDate: string;
    } | null>(null);

    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    const handleSectionClick = (section: string) => {
        setActiveSection(section);
        switch (section) {
            case "Invoices":
                navigate("/accounts");
                break;
            case "Payouts":
                navigate("/accounts/payouts");
                break;
            case "Invoice List":
                navigate("/accounts/invoicelist");
                break;
            default:
                break;
        }
    };
    const handleFilterClick = () => setIsPopupOpen(!isPopupOpen);
    const handleOutsideClick = (event: MouseEvent) => {
        if (popupRef.current &&
            !popupRef.current.contains(event.target as Node)) {
            setIsPopupOpen(false);
        }
    };


    useEffect(() => {
        if (isPopupOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isPopupOpen]);
    const tableClass = "font-base font-normal p-2 text-zinc-100 font-Montserrat";
    const rowClass = "font-base font-normal p-2 text-neutral-600 text-center font-Montserrat";

    // Example usage
    async function loadInvoices(isLoadingMore = false) {
        if (loading) return;

        setLoading(true);
        try {
            const auth = getAuth();
            const result = await fetchInvoices(db, auth.currentUser, {
                pageSize: 10,
                ...(isLoadingMore && lastDocument && {
                    lastDocumentId: lastDocument.id,
                    lastInvoiceDate: lastDocument.invoiceDate,
                }),
            });

            const response = result.data as unknown as FetchInvoicesResponse;

            setInvoices(prevInvoices =>
                isLoadingMore
                    ? [...prevInvoices, ...response.invoices]
                    : response.invoices
            );
            setHasMore(response.hasMore);

            // Handle pagination
            // if (response.hasMore) {
            // Save last document details for next page fetch
            setLastDocument(response.lastDocument);
            // Use lastDoc in next fetch call
            // }

            // Handle the received amount
            const receivedAmounts: { [key: string]: string } = {};
            const invoiceStatuses: { [key: string]: string } = {};
            response.invoices.forEach(invoice => {
                receivedAmounts[invoice.data.orderNumber] = invoice.data.receivedAmount === 0 ? '' : String(invoice.data.receivedAmount);
                invoiceStatuses[invoice.data.orderNumber] = invoice.data.status || 'Pending';
            });

            setReceivedAmounts(receivedAmounts);
            setInvoiceStatuses(invoiceStatuses);

        } catch (error) {
            console.error('Failed to fetch invoices:', error);
        } finally {
            setLoading(false);
        }
    }

    // Initial load
    useEffect(() => {
        loadInvoices()
    }, []);

    const loadMore = () => {
        if (!hasMore || loading) return;
        loadInvoices(true);
    };

    const handleStatusChange = (invoiceId: string, orderId: string, newStatus: string) => {
        setInvoiceStatuses(prev => ({
            ...prev,
            [orderId]: newStatus
        }));

        // Update the status in the database
        const auth = getAuth();
        const updatedInvoice = updateInvoice(db, auth.currentUser, invoiceId, newStatus, undefined);

        // Handle the response
        updatedInvoice.then((response: any) => {
            console.log(response.message);
        }).catch((error: any) => {
            console.error('Failed to update invoice:', error);
        });

    };
    const handleReceivedChange = (invoiceId: string, orderId: string, receivedAmount: string) => {
        setReceivedAmounts(prev => ({
            ...prev,
            [orderId]: receivedAmount
        }));

        // Update the received amount in the database
        const auth = getAuth();
        const updatedInvoice = updateInvoice(db, auth.currentUser, invoiceId, undefined, parseFloat(receivedAmount));

        // Handle the response
        updatedInvoice.then((response: any) => {
            console.log(response.message);
        }).catch((error: any) => {
            console.error('Failed to update invoice:', error);
        });
    };
    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-20">
                <TopNavbar />
            </div> */}
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full min-h-screen flex flex-col">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-6">
                            <div className="flex items-center justify-center w-12 h-12 rounded-full"
                                style={{ background: "linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)", }}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/basil_invoice-outline.svg`}
                                    alt="Icon"
                                    className="w-7 h-7" />
                            </div>
                            <div className="flex items-center space-x-4">
                                {sections.map((section) => (
                                    <h2 key={section}
                                        onClick={() => handleSectionClick(section)}
                                        className={`font-montserrat text-lg font-medium cursor-pointer ${activeSection === section
                                            ? "text-gray-900"
                                            : "text-stone-300"}`}
                                        style={{
                                            borderBottom:
                                                activeSection === section
                                                    ? "2px solid transparent"
                                                    : "none",
                                            backgroundImage:
                                                activeSection === section
                                                    ? "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)"
                                                    : "none",
                                            backgroundPosition: "bottom",
                                            backgroundSize: "100% 2px",
                                            backgroundRepeat: "no-repeat",
                                        }}>
                                        {section}
                                    </h2>))}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center px-6 gap-6">
                            <h2 className="font-montserrat text-2xl font-medium-gray-900">Invoice List</h2>
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`} alt="search-icon" className="w-7 h-7 ml-2" />
                                <input type="text"
                                    placeholder="Search by Name/Invoice #... "
                                    className="w-full px-4 py-2 bg-zinc-100 focus:outline-none rounded-3xl" />
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <button onClick={handleFilterClick} className="text-gray-600 hover:text-gray-800">
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`} alt="filter-icon" className="w-[35px] h-[35px]" />
                                </button>

                                {isPopupOpen && (
                                    <div
                                        ref={popupRef}
                                        className="absolute top-0 right-0 transform translate-x-3 w-[261px] bg-white border rounded-3xl shadow-lg p-4 z-50" >
                                        <div className="flex justify-between items-center">
                                            <h3 className="text-[14px] font-normal">Filters</h3>
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                                alt="filter-icon"
                                                onClick={() => setIsPopupOpen(false)}
                                                className="w-9 h-9 cursor-pointer" />
                                        </div>
                                        <div className="mt-4">
                                            <span className="text-[12px] font-normal">Status:</span>
                                            <div className="mt-2 space-y-2">
                                                {['Paid', 'Pending', 'Overdue'].map(status => (
                                                    <label key={status} className="flex items-center space-x-2 cursor-pointer">
                                                        <div className="relative">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedStatuses.includes(status)}
                                                                onChange={() => { }}
                                                                className="peer appearance-none h-5 w-5 border-2 border-gray-300 rounded-sm cursor-pointer checked:border-orange-500 checked:bg-white hover:border-orange-500 transition-colors" />
                                                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1 -translate-y-1.5 opacity-0 peer-checked:opacity-100 pointer-events-none">
                                                                <div className="w-2 h-2 rounded-sm bg-orange-500"></div>
                                                            </div>
                                                        </div>
                                                        <span className="text-[12px]">{status}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex justify-between space-x-3 mt-4">
                                            <button
                                                onClick={() => setIsPopupOpen(false)}
                                                className="px-8 py-2 text-white bg-black rounded-full text-sm">
                                                Cancel
                                            </button>
                                            <button
                                                onClick={() => setIsPopupOpen(false)}
                                                className="px-8 py-2 text-white bg-cyan-600 rounded-full text-sm">
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="rounded-3xl overflow-hidden shadow-lg">
                        <div className="overflow-x-auto">
                            <table className="min-w-full table-auto bg-zinc-100">
                                <thead>
                                    <tr className="text-center bg-sky-950  ">
                                        <th className={tableClass}>Date</th>
                                        <th className={tableClass}>Invoice #</th>
                                        <th className={tableClass}>Order #</th>
                                        <th className={tableClass}>Customer Name</th>
                                        <th className={tableClass}>Status</th>
                                        <th className={tableClass}>Due Date</th>
                                        <th className={tableClass}>Amount</th>
                                        <th className={tableClass}>Received</th>
                                        <th className={tableClass}>Bal.</th>
                                        <th className={tableClass}>Download Invoice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((invoice, index) => (
                                        <tr key={index} className="border-b">
                                            <td className={rowClass}>{invoice.data.invoiceDate}</td>
                                            <td className={rowClass}>{invoice.data.invoiceNumber}</td>
                                            <td className={rowClass}>{invoice.data.orderNumber}</td>
                                            <td className={rowClass}>{invoice.data?.customer?.firstName + " " + invoice.data?.customer?.lastName}</td>
                                            <td className={rowClass}>
                                                <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                                    <div className="relative">
                                                        <select
                                                            className="w-full py-2 pl-4 pr-10 bg-white text-gray-800 rounded-3xl focus:outline-none appearance-none"
                                                            value={invoiceStatuses[invoice.data.orderNumber] || "Pending"}
                                                            onChange={(e) => handleStatusChange(invoice.id, invoice.data.orderNumber, e.target.value)}
                                                        >
                                                            {statusOptions.map((status) => (
                                                                <option key={status} value={status}>
                                                                    {status}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                            <img
                                                                src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                                                alt="Dropdown Arrow"
                                                                className="w-7 h-7 transition-transform duration-300"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className={rowClass}>{invoice.data.dueDate}</td>
                                            <td className={rowClass}>{invoice?.data.table?.reduce((total: number, item: { amount: number; }) => total + item.amount, 0)}</td>
                                            <td className={rowClass}>
                                                <input
                                                    type="text"
                                                    className=" w-28 px-4 py-1 rounded-3xl border-2 border-sky-900 focus:outline-none focus:border-sky-500 text-center"
                                                    placeholder="Enter amount"
                                                    value={receivedAmounts[invoice.data.orderNumber] || ''}
                                                    onChange={(e) => handleReceivedChange(invoice.id, invoice.data.orderNumber, e.target.value)}
                                                />
                                            </td>
                                            <td className={rowClass}>
                                                {invoice?.data.table?.reduce((total: number, item: { amount: number; }) => total + item.amount, 0) - parseFloat(receivedAmounts[invoice.data.orderNumber] ||
                                                    '0')}
                                            </td>
                                            <td className="p-2 text-left">
                                                <img
                                                    src={`${process.env.PUBLIC_URL}/images/desktop/tdesign_file-download.svg`}
                                                    alt="download-icon"
                                                    className="w-4 h-4 mx-auto cursor-pointer"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <hr className="mt-8 mb-6 w-full" style={{ border: "none", height: "2px", background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)", }} />
                    <div className="flex items-center justify-center h-full">
                        {/* <button onClick={loadMore} className=" text-center p-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] mt-5 mb-5 rounded-3xl font-medium hover:bg-gray-50 ">
                            Test load
                        </button> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvoiceListAccount;